<template>
    <div class="mt-10 rounded-md bg-white p-6 shadow-md">
        <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="flex flex-col sm:flex-row sm:items-center">
                <div
                    class="h-20 w-20 shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                >
                    <img
                        :src="selectedTeam.profile_photo_url"
                        class="h-20 w-20 rounded-full bg-white object-cover"
                        alt="Team profile photo"
                    />
                </div>
                <div class="mt-2 flex flex-col sm:ml-10 sm:mt-0">
                    <h2 class="text-2xl font-bold text-gray-800">
                        {{ selectedTeam.name }}
                    </h2>
                    <div class="text-xl font-medium text-gray-500">
                        {{ selectedTeam.email }}
                    </div>
                </div>
            </div>

            <div
                class="flex flex-col flex-wrap gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
                <AtomicButton
                    size="sm"
                    color="yellow"
                    @click="syncTeamWithHubspot(selectedTeam)"
                >
                    <template #icon>
                        <BriefcaseIcon class="mr-2 h-6 w-6" />
                    </template>

                    <div v-if="selectedTeam.hubspot_deal_id">
                        {{ t("vue.sync_with_hubspot") }}
                    </div>
                    <div v-else>
                        {{ t("vue.sync_into_hubspot") }}
                    </div>
                </AtomicButton>

                <AtomicButton
                    size="sm"
                    color="purple"
                    @click="handleImpersonate(selectedTeam)"
                >
                    <template #icon>
                        <EyeIcon class="mr-2 h-6 w-6" />
                    </template>
                    {{ t("vue.impersonate") }}
                </AtomicButton>

                <AtomicButton
                    size="lg"
                    color="red"
                    @click="handleDeleteTeam(selectedTeam)"
                >
                    <template #icon>
                        <TrashIcon class="mr-2 h-6 w-6" />
                    </template>
                    {{ t("vue.delete_team") }}
                </AtomicButton>
            </div>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="flex flex-col">
                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.account_id") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.id }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.created") }}
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{
                            format(
                                new Date(selectedTeam.created_at),
                                "d. L. yyyy '('HH:mm:ss')'",
                            )
                        }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.domains_count") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.domains_quantity }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_type") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.subscription_type }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_period") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.subscription_period }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_amount") }}
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.paddle_sub_amount }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.paddle_sub_last_transaction_amount") }}
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.paddle_sub_last_transaction_amount }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import { BriefcaseIcon, EyeIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";

import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import useConfirm from "@/composables/useConfirm";
import { create } from "@/composables/useNotifications";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import axios from "axios";
import type IUser from "@/types/Members/IUser.ts";

defineProps<{
    selectedTeam: ICurrentTeam;
}>();

// Get access to translations
const { t } = useTranslations();

const { show } = useConfirm();

const handleDeleteTeam = async (selectedTeam: ICurrentTeam) => {
    const confirmed = await show({
        header: `${t("vue.delete_team_header")} ${selectedTeam.name}`,
        message: `${t("vue.delete_team_message")} ${selectedTeam.name}?`,
        type: "danger",
        confirm: t("vue.delete_team_button"),
    });
    if (confirmed) {
        await deleteTeam(selectedTeam);
    }
};

const handleImpersonate = (selectedTeam: ICurrentTeam) => {
    router.get(
        route("kiosk.users.impersonate", { user: selectedTeam.user_id }),
    );
};

const syncTeamWithHubspot = async (selectedTeam: ICurrentTeam) => {
    const res = await axios.post(
        route("kiosk.teams.hubspot.sync", { team: selectedTeam.id }),
    );

    if (res.data.success) {
        create({
            title: t("vue.success"),
            type: "success",
        });
        router.get(
            route("kiosk.teams.feature-management.index", {
                team: selectedTeam.id,
            }),
        );
    } else {
        create({
            title: t("vue.error"),
            text: res.data.message,
            type: "error",
        });
    }
};

const deleteTeam = async (selectedTeam: ICurrentTeam) => {
    try {
        const res = await axios.delete(
            route("kiosk.teams.delete", { team: selectedTeam.id }),
        );
        if (res.data.success) {
            create({
                title: t("vue.team_deleted"),
                type: "success",
            });
            router.get(route("kiosk.teams.index"));
        } else {
            create({
                title: t("vue.team_not_deleted"),
                text: t("vue.error_deleting_team"),
                type: "error",
            });
        }
    } catch (err) {
        create({
            title: t("vue.team_not_deleted"),
            text: t("vue.error_deleting_team"),
            type: "error",
        });
    }
};
</script>
